'use client';

import { Trash2, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { WorkloadAssignment } from '../types';
import { AssignmentStatusBadge } from './assignment-status-badge';

interface WorkloadCompactRowProps {
  assignment: WorkloadAssignment;
  onDelete: (assignment: WorkloadAssignment) => void;
  onClick?: (assignment: WorkloadAssignment) => void;
}

export function WorkloadCompactRow({
  assignment,
  onDelete,
  onClick
}: WorkloadCompactRowProps) {
  const operatorName = assignment.operator?.name ?? '';
  const deviceName = assignment.device?.name ?? '';

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border px-4 py-2.5',
        onClick && 'hover:bg-accent/50 cursor-pointer'
      )}
      onClick={() => onClick?.(assignment)}
    >
      {/* Left: assignment number + operator */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-mono text-sm font-medium'>
            {assignment.assignment_number}
          </span>
          {operatorName && (
            <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
              {operatorName}
            </span>
          )}
        </div>
      </div>

      {/* Center: device name + boxes count (hidden on mobile) */}
      {deviceName && (
        <span className='text-muted-foreground hidden text-sm md:inline'>
          {deviceName}
        </span>
      )}
      {assignment.boxes_count != null && (
        <Badge variant='secondary' className='hidden gap-1 md:inline-flex'>
          <Package className='h-3.5 w-3.5' />
          {assignment.boxes_count}
        </Badge>
      )}

      {/* Right: status badge + action buttons */}
      <AssignmentStatusBadge status={assignment.status} />

      <div className='flex shrink-0 items-center'>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
          onClick={(e) => {
            e.stopPropagation();
            onDelete(assignment);
          }}
        >
          <Trash2 className='h-3.5 w-3.5' />
        </Button>
      </div>
    </div>
  );
}
